import { aiDecisionSchema, type AIDecision } from '@cadencia/contracts';
import { newSyncColumns, putRow, type WriteContext } from '../writes.js';

/**
 * El veredicto del dominio sobre una propuesta de la IA
 * (docs/AI_ARCHITECTURE.md §8, punto 5).
 *
 * `ai_decisions.accepted` nace en NULO. Lo pone el servidor asi a proposito:
 * la Edge Function valida la forma y nada mas, y marcar como aceptado lo que
 * nadie ha juzgado seria mentir en la direccion peor. Este modulo es quien lo
 * resuelve, ya en el cliente, despues de aplicar las reglas de dominio.
 *
 * POR QUE SE ESCRIBE LA FILA ENTERA Y NO UN `UPDATE` DE UN CAMPO
 *
 * Porque la fila la creo el SERVIDOR y todavia no ha bajado: acaba de
 * insertarse y el `sync_pull` que la traiga puede tardar. Escribir solo
 * `accepted` sobre una fila que no existe en Dexie no actualizaria nada.
 *
 * El cliente tiene todo lo necesario para reconstruirla —el `aiRequestId` y el
 * `payload` vienen en la misma respuesta—, y `sync_push` resuelve con
 * `on conflict (id) do update`, asi que la escritura converge con la fila del
 * servidor en vez de duplicarla. La politica de conflicto de `ai_decisions` es
 * `document`, y esta version es posterior.
 *
 * NO ES CRITICO. Si esto falla, la propuesta sigue su curso: el veredicto es
 * el registro de una decision que ya se tomo, no la decision. Por eso quien
 * llama no espera a que termine ni deja de funcionar si no termina.
 */
export interface VerdictInput {
  /** La fila que creo la Edge Function. */
  decisionId: string;
  aiRequestId: string;
  payload: Record<string, unknown>;
  accepted: boolean;
  /** Por que se rechazo. `null` cuando se acepto: no hay nada que explicar. */
  rejectionReason: string | null;
}

export async function recordAIVerdict(
  ctx: WriteContext,
  input: VerdictInput,
): Promise<AIDecision> {
  const existente = await ctx.db.aiDecisions.get(input.decisionId);

  const fila = aiDecisionSchema.parse({
    ...(existente ?? newSyncColumns(ctx, input.decisionId)),
    aiRequestId: input.aiRequestId,
    // `decisionId` apunta a una fila de `training_decisions`, que es otra cosa:
    // solo lo tienen las propuestas que llegan a convertirse en un cambio del
    // plan. Una redaccion no lo tiene nunca.
    decisionId: existente?.decisionId ?? null,
    payload: input.payload,
    accepted: input.accepted,
    rejectionReason: input.rejectionReason === null ? null : input.rejectionReason.slice(0, 300),
  });

  return putRow(ctx, 'aiDecisions', fila);
}
